import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChatBubbleLeftRightIcon, 
  UserIcon, 
  PhoneIcon, 
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useLive } from "../Live/LiveProvider";
import PresenceBadge from "../Live/PresenceBadge";
import CopyPhone from "../Live/CopyPhone";

const AgentPreview = ({ agent, onChatClick, binId }) => {
  const navigate = useNavigate();
  const live = useLive();
  if (!agent) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100">
            <UserIcon className="h-6 w-6 text-amber-700" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-amber-950">No agent selected</h3>
            <p className="mt-1 text-sm text-amber-800">
              Select an agent to validate your materials before selling.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const displayName =
    [agent.firstName, agent.lastName].filter(Boolean).join(" ").trim() ||
    agent.username ||
    agent.agentDetails?.agent_name ||
    "Agent";

  const getWorkingHours = () => {
    if (!agent.agentDetails?.openHours) return 'Not specified';

    const today = new Date()
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    const dayHours = agent.agentDetails.openHours[today];
    
    if (!dayHours || !dayHours.open || !dayHours.close) {
      return 'Not specified';
    }
    
    return `${dayHours.open} - ${dayHours.close}`;
  };

  const locationLine = [
    agent.agentDetails?.streetAddress,
    agent.agentDetails?.localGovernmentAddress,
    agent.state || agent.homeAddress,
  ].filter(Boolean);

  const quickActions = [
    { label: "Call", title: "Call agent", icon: PhoneIcon, onClick: () => live?.startCall(binId, agent._id || agent.id) },
    { label: "Map", title: "Open the meeting map", icon: MapPinIcon, onClick: () => live?.openMeet(binId) },
    { label: "Chat", title: "Chat with agent", icon: ChatBubbleLeftRightIcon, onClick: () => onChatClick(binId) },
  ];

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="bg-gradient-to-br from-emerald-800 via-green-600 to-emerald-500 px-4 py-5 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/15 ring-2 ring-white/30 sm:h-16 sm:w-16">
              {agent.agentDetails?.agentImage ? (
                <img
                  src={agent.agentDetails.agentImage}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <UserIcon className="h-7 w-7 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-100">
                Your agent
              </p>
              <h3 className="truncate text-lg font-semibold text-white sm:text-xl">
                {displayName}
              </h3>
              <p className="mt-0.5 text-sm text-emerald-50/90">
                Validates your bin and collects the materials
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:max-w-xs lg:w-60 lg:shrink-0">
            {quickActions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                title={action.title}
                className="flex flex-col items-center justify-center gap-1 rounded-xl bg-white/15 px-2 py-2.5 text-white transition hover:bg-white/25"
              >
                <action.icon className="h-5 w-5" />
                <span className="text-xs font-semibold">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4 sm:p-6">
        {agent.spotlightLocations && agent.spotlightLocations.length > 0 && (
          <div>
            <h4 className="mb-2 flex items-center text-sm font-semibold text-gray-800">
              <MapPinIcon className="mr-2 h-4 w-4 text-green-600" />
              Pickup spots
            </h4>
            <ul className="grid gap-2 sm:grid-cols-2">
              {agent.spotlightLocations.map((item, index) => (
                <li key={index} className="rounded-xl bg-gray-50 px-3 py-2.5 text-sm text-gray-800">
                  <span className="font-medium">{item.location || '—'}</span>
                  {item.time && <span className="mt-0.5 block text-gray-500">{item.time}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-3">
            <div className="mb-1 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
              <PhoneIcon className="mr-1.5 h-4 w-4" />
              Phone
            </div>
            <CopyPhone phone={agent.phoneNumber || agent.agentDetails?.phone_number} />
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-3">
            <div className="mb-1 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
              <ClockIcon className="mr-1.5 h-4 w-4" />
              Today
            </div>
            <p className="font-medium text-gray-900">{getWorkingHours()}</p>
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-3 sm:col-span-2">
            <div className="mb-1 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
              <MapPinIcon className="mr-1.5 h-4 w-4" />
              Location
            </div>
            {locationLine.length > 0 ? (
              locationLine.map((line) => (
                <p key={line} className="text-sm font-medium text-gray-900">{line}</p>
              ))
            ) : (
              <p className="text-sm text-gray-500">Not provided</p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <PresenceBadge userId={agent._id || agent.id} initialActive={agent.presence?.active} />
            <span className="inline-flex items-center text-sm font-medium text-gray-600">
              <CheckCircleIcon className="mr-1 h-4 w-4 text-green-600" />
              Verified agent
            </span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:min-w-[280px]">
            <button
              type="button"
              onClick={() => onChatClick(binId)}
              className="inline-flex items-center justify-center rounded-xl bg-green-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-green-700"
            >
              <ChatBubbleLeftRightIcon className="mr-2 h-5 w-5" />
              Chat
            </button>
            <button
              type="button"
              onClick={() => navigate("/agents")}
              className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              <ArrowPathIcon className="mr-2 h-5 w-5" />
              Change agent
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgentPreview;
